import { useState, useCallback } from 'react';
import { competitorApi } from '../api/competitors';
import type { CompetitorAnalysis, AnalyzePayload } from '../api/competitors';

export function useCompetitors() {
  const [analyses, setAnalyses] = useState<CompetitorAnalysis[]>([]);
  const [activeAnalysis, setActiveAnalysis] = useState<CompetitorAnalysis | null>(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const fetchAnalyses = useCallback(async (p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await competitorApi.list(p);
      setAnalyses(res.data.data);
      setTotal(res.data.meta.total);
      setLastPage(res.data.meta.last_page);
      setPage(p);
    } catch {
      setError('Failed to load competitor analyses.');
    } finally {
      setLoading(false);
    }
  }, []);

  const analyze = useCallback(async (payload: AnalyzePayload) => {
    setAnalyzing(true);
    setError(null);
    try {
      const res = await competitorApi.analyze(payload);
      const analysis = res.data.data;
      setActiveAnalysis(analysis);
      setAnalyses((prev) => [analysis, ...prev]);
      return analysis;
    } catch (err: any) {
      const msg = err?.message ?? 'Competitor analysis failed.';
      setError(msg);
      return null;
    } finally {
      setAnalyzing(false);
    }
  }, []);

  const deleteAnalysis = useCallback(async (id: number) => {
    try {
      await competitorApi.destroy(id);
      setAnalyses((prev) => prev.filter((a) => a.id !== id));
      if (activeAnalysis?.id === id) setActiveAnalysis(null);
    } catch {
      setError('Failed to delete analysis.');
    }
  }, [activeAnalysis]);

  return {
    analyses, activeAnalysis, loading, analyzing, error, total, page, lastPage,
    fetchAnalyses, analyze, deleteAnalysis, setActiveAnalysis, setError,
  };
}
